import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";

export default function Timeline({ milestones = [] }) {
  const containerRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start 0.85", "end 0.4"],
  });
  const lineHeight = useTransform(scrollYProgress, [0, 1], ["0%", "100%"]);

  const active = milestones.filter((m) => m.isActive !== false);
  // On small screens, render cards visible immediately — entrance animations
  // depend on IntersectionObserver behaviour that varies across mobile browsers.
  const animate = typeof window !== "undefined" && window.matchMedia("(min-width: 768px)").matches;

  return (
    <div ref={containerRef} className="relative max-w-5xl mx-auto" data-testid="milestone-timeline">
      {/* Track */}
      <div className="absolute left-5 md:left-1/2 top-0 bottom-0 w-[2px] md:-translate-x-1/2 bg-gray-200" />
      <motion.div
        style={{ height: lineHeight }}
        className="absolute left-5 md:left-1/2 top-0 w-[2px] md:-translate-x-1/2 bg-gradient-to-b from-gold via-gold to-navy"
      />

      <div className="space-y-12 md:space-y-20">
        {active.map((m, i) => {
          const isLeft = i % 2 === 0;
          return (
            <div
              key={m.id}
              className={`relative pl-14 md:pl-0 md:grid md:grid-cols-2 md:gap-12 items-center ${
                isLeft ? "" : "md:[direction:rtl]"
              }`}
            >
              {/* Node: rotated square (Swiss accent) */}
              <motion.div
                initial={animate ? { scale: 0 } : false}
                whileInView={{ scale: 1 }}
                viewport={{ once: true, amount: 0.4 }}
                transition={{ duration: 0.4, type: "spring" }}
                className="absolute left-5 md:left-1/2 -translate-x-1/2 top-2 md:top-1/2 md:-translate-y-1/2 z-10"
              >
                <span className="block h-4 w-4 bg-gold rotate-45 ring-4 ring-white shadow-[0_0_0_1px_rgba(234,88,12,0.3)]" />
              </motion.div>

              {/* Card */}
              <motion.div
                initial={animate ? { opacity: 0, y: 30 } : false}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.15 }}
                transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                className={`[direction:ltr] ${isLeft ? "md:text-right md:pr-4" : "md:col-start-2 md:pl-4"}`}
              >
                <div className="bg-white rounded-2xl overflow-hidden border border-gray-200 card-lift group">
                  {m.image && (
                    <div className="h-44 overflow-hidden">
                      <img
                        src={m.image}
                        alt={m.title}
                        className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-700"
                      />
                    </div>
                  )}
                  <div className="p-6">
                    <div className={`flex items-center gap-3 mb-3 ${isLeft ? "md:justify-end" : ""}`}>
                      <span className="font-mono text-xl font-bold text-gold">{m.year}</span>
                      {m.category && (
                        <span className="px-2.5 py-1 rounded-full font-mono text-[9px] uppercase tracking-widest font-bold bg-navy text-white">
                          {m.category}
                        </span>
                      )}
                    </div>
                    <h3 className="font-display text-xl font-bold text-navy mb-2">{m.title}</h3>
                    <p className="text-sm text-gray-600 leading-relaxed">{m.description}</p>
                  </div>
                </div>
              </motion.div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
